import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { subMonths, format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Music2, Coins, Users, BookOpen, Radio, Wheat,
  AlertTriangle, TrendingUp,
  CheckCircle2, Clock, ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatNumber } from '../../utils/format';

// ─── Types locaux ───────────────────────────────────────────────
interface DeptSummary {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;        // bg Tailwind
  accent: string;       // border/text
  route: string;
  reportCount: number;
  lastReportDate: string | null;
  kpis: { label: string; value: string | number }[];
  loaded: boolean;
}

interface NeedSummary {
  total: number;
  high: number;
  medium: number;
  low: number;
}

// ─── Composants utilitaires ─────────────────────────────────────
function KpiChip({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] text-gray-500 uppercase tracking-wide leading-none mb-0.5">{label}</span>
      <span className="text-sm font-semibold text-gray-800">{value}</span>
    </div>
  );
}

function DeptCard({ dept }: { dept: DeptSummary }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden`}>
      {/* Header */}
      <div className={`${dept.color} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className={`${dept.accent} p-1.5 rounded-lg bg-white/20`}>
            {dept.icon}
          </div>
          <span className="font-semibold text-sm text-white">{dept.label}</span>
        </div>
        <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-medium">
          {dept.reportCount} rapport{dept.reportCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* KPIs */}
      <div className="px-4 py-3">
        {!dept.loaded ? (
          <div className="animate-pulse space-y-2">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        ) : dept.reportCount === 0 ? (
          <p className="text-xs text-gray-400 italic">Aucun rapport sur la période</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {dept.kpis.map((kpi, i) => (
              <KpiChip key={i} label={kpi.label} value={kpi.value} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">
          {dept.lastReportDate
            ? `Dernier : ${format(parseISO(dept.lastReportDate), 'd MMM yyyy', { locale: fr })}`
            : 'Aucun rapport'}
        </span>
        <Link
          to={dept.route}
          className="text-[10px] font-medium text-[#00665C] hover:underline flex items-center gap-0.5"
        >
          Voir <ChevronRight size={10} />
        </Link>
      </div>
    </div>
  );
}

// ─── Composant principal ────────────────────────────────────────
export function ConsolidatedView() {
  const [startDate, setStartDate] = useState(format(subMonths(new Date(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const [needs, setNeeds] = useState<NeedSummary>({ total: 0, high: 0, medium: 0, low: 0 });
  const [totalReports, setTotalReports] = useState(0);

  const [depts, setDepts] = useState<DeptSummary[]>([
    {
      id: 'worship',
      label: 'Culte & Adoration',
      icon: <Music2 size={16} className="text-white" />,
      color: 'bg-[#00665C]',
      accent: 'text-[#00665C]',
      route: '/worship-reports',
      reportCount: 0, lastReportDate: null, kpis: [], loaded: false,
    },
    {
      id: 'finance',
      label: 'Finances',
      icon: <Coins size={16} className="text-white" />,
      color: 'bg-[#F2B636]',
      accent: 'text-[#F2B636]',
      route: '/finance-reports',
      reportCount: 0, lastReportDate: null, kpis: [], loaded: false,
    },
    {
      id: 'adn',
      label: 'ADN / Visiteurs',
      icon: <Users size={16} className="text-white" />,
      color: 'bg-indigo-600',
      accent: 'text-indigo-600',
      route: '/adn-reports',
      reportCount: 0, lastReportDate: null, kpis: [], loaded: false,
    },
    {
      id: 'academie',
      label: 'Académie Biblique',
      icon: <BookOpen size={16} className="text-white" />,
      color: 'bg-purple-600',
      accent: 'text-purple-600',
      route: '/academie-reports',
      reportCount: 0, lastReportDate: null, kpis: [], loaded: false,
    },
    {
      id: 'sono',
      label: 'Sono & Médias',
      icon: <Radio size={16} className="text-white" />,
      color: 'bg-sky-600',
      accent: 'text-sky-600',
      route: '/sono-reports',
      reportCount: 0, lastReportDate: null, kpis: [], loaded: false,
    },
    {
      id: 'sainteCene',
      label: 'Sainte-Cène',
      icon: <Wheat size={16} className="text-white" />,
      color: 'bg-rose-600',
      accent: 'text-rose-600',
      route: '/sainte-cene-reports',
      reportCount: 0, lastReportDate: null, kpis: [], loaded: false,
    },
  ]);

  const [globalStats, setGlobalStats] = useState({
    avgAttendance: 0,
    totalFinances: 0,
    totalVisitors: 0,
    financesTrend: 0,
    attendanceTrend: 0,
  });

  const fetchAll = async () => {
    setLoading(true);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const startISO = start.toISOString();
    const endISO = end.toISOString();

    try {
      // Fetch all collections in parallel
      const [
        worshipSnap, financeSnap, adnSnap,
        academieSnap, sonoSnap, sceneSnap, needsSnap
      ] = await Promise.all([
        getDocs(query(
          collection(db, 'worshipReports'),
          where('date', '>=', startISO), where('date', '<=', endISO),
          orderBy('date', 'desc')
        )),
        getDocs(query(
          collection(db, 'financeReports'),
          where('date', '>=', startISO), where('date', '<=', endISO),
          orderBy('date', 'desc')
        )),
        getDocs(query(
          collection(db, 'adnReports'),
          where('date', '>=', startISO), where('date', '<=', endISO),
          orderBy('date', 'desc')
        )),
        getDocs(query(
          collection(db, 'academieReports'),
          where('date', '>=', startISO), where('date', '<=', endISO),
          orderBy('date', 'desc')
        )),
        getDocs(query(
          collection(db, 'sonoReports'),
          where('date', '>=', startISO), where('date', '<=', endISO),
          orderBy('date', 'desc')
        )),
        getDocs(query(
          collection(db, 'sainteCeneReports'),
          where('date', '>=', startISO), where('date', '<=', endISO),
          orderBy('date', 'desc')
        )),
        getDocs(query(
          collection(db, 'needs'),
          where('isAddressed', '==', false)
        )),
      ]);

      const worship = worshipSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      const finance = financeSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      const adn = adnSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      const academie = academieSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      const sono = sonoSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      const scene = sceneSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      const needsList = needsSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];

      // ── Besoins ────────────────────────────────────────────────
      setNeeds({
        total: needsList.length,
        high: needsList.filter(n => n.priority === 'high').length,
        medium: needsList.filter(n => n.priority === 'medium').length,
        low: needsList.filter(n => n.priority === 'low').length,
      });

      const tot = worship.length + finance.length + adn.length + academie.length + sono.length + scene.length;
      setTotalReports(tot);

      // ── Global stats ───────────────────────────────────────────
      const avgAtt = worship.length > 0
        ? Math.round(worship.reduce((s: number, r: any) => s + (r.totalParticipants ?? 0), 0) / worship.length)
        : 0;
      const totalFin = finance.reduce((s: number, r: any) => s + (r.totalFinances ?? 0), 0);
      const totalVis = adn.reduce((s: number, r: any) => s + (r.totalNewVisitors ?? 0), 0);

      setGlobalStats({ avgAttendance: avgAtt, totalFinances: totalFin, totalVisitors: totalVis, financesTrend: 0, attendanceTrend: 0 });

      // ── Culte ──────────────────────────────────────────────────
      const avgNewMembers = worship.length > 0
        ? Math.round(worship.reduce((s: number, r: any) => s + (r.totalNewMembers ?? 0), 0) / worship.length)
        : 0;
      const avgChildren = worship.length > 0
        ? Math.round(worship.reduce((s: number, r: any) =>
            s + (r.attendance?.children?.boys ?? 0) + (r.attendance?.children?.girls ?? 0), 0
          ) / worship.length)
        : 0;

      // ── Finance breakdown ──────────────────────────────────────
      const totalTithes = finance.reduce((s: number, r: any) => s + (r.finances?.tithes ?? 0), 0);
      const totalOfferings = finance.reduce((s: number, r: any) =>
        s + (r.finances?.regularOfferings ?? 0) + (r.finances?.specialOfferings ?? 0), 0);

      // ── ADN ───────────────────────────────────────────────────
      const totalWantsJoin = adn.reduce((s: number, r: any) => s + (r.totalWantsToJoin ?? 0), 0);
      const totalGiveLife = adn.reduce((s: number, r: any) => s + (r.totalWantsToGiveLifeToJesus ?? 0), 0);

      // ── Académie ───────────────────────────────────────────────
      const totalActual = academie.reduce((s: number, r: any) => s + (r.actualStudents ?? 0), 0);
      const totalPresent = academie.reduce((s: number, r: any) => s + (r.presentStudents ?? 0), 0);
      const attendRate = totalActual > 0 ? Math.round((totalPresent / totalActual) * 100) : 0;

      // ── Sono — statuts OK/NOK ──────────────────────────────────
      const lastSono = sono[0];
      const sonoOk = lastSono
        ? Object.values(lastSono.beforeService ?? {}).filter((v: any) => v === 'OK').length
        : 0;
      const sonoTotal = lastSono ? Object.values(lastSono.beforeService ?? {}).length : 0;

      // ── Sainte-Cène ────────────────────────────────────────────
      const totalPains = scene.reduce((s: number, r: any) => s + (r.painsDistribuees ?? 0), 0);
      const totalVins = scene.reduce((s: number, r: any) => s + (r.vinsDistribuees ?? 0), 0);

      setDepts(prev => prev.map(d => {
        switch (d.id) {
          case 'worship': return {
            ...d, reportCount: worship.length,
            lastReportDate: worship[0]?.date ?? null,
            loaded: true,
            kpis: [
              { label: 'Moy. participants', value: avgAtt },
              { label: 'Moy. nouveaux', value: avgNewMembers },
              { label: 'Moy. enfants', value: avgChildren },
              { label: 'Cultes', value: worship.length },
            ],
          };
          case 'finance': return {
            ...d, reportCount: finance.length,
            lastReportDate: finance[0]?.date ?? null,
            loaded: true,
            kpis: [
              { label: 'Total', value: `${formatNumber(totalFin)} F` },
              { label: 'Dîmes', value: `${formatNumber(totalTithes)} F` },
              { label: 'Offrandes', value: `${formatNumber(totalOfferings)} F` },
              { label: 'Rapports', value: finance.length },
            ],
          };
          case 'adn': return {
            ...d, reportCount: adn.length,
            lastReportDate: adn[0]?.date ?? null,
            loaded: true,
            kpis: [
              { label: 'Visiteurs', value: totalVis },
              { label: 'Veut rejoindre', value: totalWantsJoin },
              { label: 'Décision Christ', value: totalGiveLife },
              { label: 'Rapports', value: adn.length },
            ],
          };
          case 'academie': return {
            ...d, reportCount: academie.length,
            lastReportDate: academie[0]?.date ?? null,
            loaded: true,
            kpis: [
              { label: 'Taux présence', value: `${attendRate}%` },
              { label: 'Présents total', value: totalPresent },
              { label: 'Inscrits total', value: totalActual },
              { label: 'Sessions', value: academie.length },
            ],
          };
          case 'sono': return {
            ...d, reportCount: sono.length,
            lastReportDate: sono[0]?.date ?? null,
            loaded: true,
            kpis: lastSono
              ? [
                  { label: 'Checks OK', value: `${sonoOk}/${sonoTotal}` },
                  { label: 'Live stream', value: lastSono.duringService?.liveStreaming ?? '-' },
                  { label: 'Son salle', value: lastSono.duringService?.roomSoundQuality === 'SATISFAISANT' ? '✓' : '⚠' },
                  { label: 'Rapports', value: sono.length },
                ]
              : [{ label: 'Rapports', value: 0 }],
          };
          case 'sainteCene': return {
            ...d, reportCount: scene.length,
            lastReportDate: scene[0]?.date ?? null,
            loaded: true,
            kpis: [
              { label: 'Pains distribués', value: totalPains },
              { label: 'Vins distribués', value: totalVins },
              { label: 'Célébrations', value: scene.length },
            ],
          };
          default: return d;
        }
      }));
    } catch (err) {
      console.error('ConsolidatedView fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  return (
    <div className="space-y-6">
      {/* ── Sélecteur de période ─────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Début</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fin</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
          />
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="px-5 py-2 bg-[#00665C] text-white text-sm font-medium rounded-lg hover:bg-[#004d45] disabled:opacity-50 transition-colors"
        >
          {loading ? 'Chargement…' : 'Actualiser'}
        </button>

        {/* Raccourcis période */}
        <div className="flex gap-2 ml-auto flex-wrap">
          {[
            { label: '7 j', months: 0, days: 7 },
            { label: '1 mois', months: 1, days: 0 },
            { label: '3 mois', months: 3, days: 0 },
            { label: '6 mois', months: 6, days: 0 },
          ].map(({ label, months, days }) => (
            <button
              key={label}
              onClick={() => {
                const end = new Date();
                const start = new Date();
                if (days) start.setDate(start.getDate() - days);
                else start.setMonth(start.getMonth() - months);
                setStartDate(format(start, 'yyyy-MM-dd'));
                setEndDate(format(end, 'yyyy-MM-dd'));
              }}
              className="px-2.5 py-1 text-xs rounded-full border border-gray-300 hover:border-[#00665C] hover:text-[#00665C] text-gray-600 transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPIs globaux ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Rapports soumis', value: totalReports, icon: <CheckCircle2 size={18} />, color: 'text-[#00665C]' },
          { label: 'Moy. participants', value: globalStats.avgAttendance, icon: <Users size={18} />, color: 'text-indigo-600' },
          { label: 'Total finances', value: `${formatNumber(globalStats.totalFinances)} F`, icon: <Coins size={18} />, color: 'text-[#F2B636]' },
          { label: 'Visiteurs accueillis', value: globalStats.totalVisitors, icon: <TrendingUp size={18} />, color: 'text-purple-600' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                <p className="text-xl font-bold text-gray-900">{loading ? '…' : stat.value}</p>
              </div>
              <span className={stat.color}>{stat.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Cartes départements ───────────────────────────────── */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Par département
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {depts.map(dept => (
            <DeptCard key={dept.id} dept={dept} />
          ))}
        </div>
      </div>

      {/* ── Besoins en attente ────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h2 className="font-semibold text-gray-800">Besoins non traités</h2>
          </div>
          <Link to="/needs-management" className="text-xs text-[#00665C] hover:underline flex items-center gap-0.5 font-medium">
            Gérer <ChevronRight size={12} />
          </Link>
        </div>
        <div className="p-5 grid grid-cols-3 gap-4">
          {[
            { label: 'Urgent', count: needs.high, color: 'bg-red-100 text-red-700 border-red-200' },
            { label: 'Moyen', count: needs.medium, color: 'bg-amber-100 text-amber-700 border-amber-200' },
            { label: 'Faible', count: needs.low, color: 'bg-gray-100 text-gray-600 border-gray-200' },
          ].map(({ label, count, color }) => (
            <div key={label} className={`rounded-lg border p-3 text-center ${color}`}>
              <p className="text-2xl font-bold">{loading ? '…' : count}</p>
              <p className="text-xs font-medium mt-0.5">{label}</p>
            </div>
          ))}
        </div>
        {needs.total > 0 && (
          <div className="px-5 pb-4">
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              <Clock size={14} />
              <span><strong>{needs.total}</strong> besoin{needs.total > 1 ? 's' : ''} en attente de traitement</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
